import React, { Component } from "react";
import transparentGrid from "./transparent-grid.jpg";
import { toast } from "react-toastify";

import BrandRequest from "./models/BrandRequest";
import DataService, { id } from "./DataService";

import {
  row,
  colAuto,
  mb2,
  ml2,
  mlAuto,
  btnOutlineDark,
  btnSuccess,
  col,
  btn,
  btnPrimary,
  btnLink,
  formGroup,
  formControl,
  alignSelfCenter,
  dFlex,
  px5,
  alignItemsCenter
} from "bootstrap/dist/css/bootstrap.css";

import {
  transparencyGrid,
  logoPreview,
  logoThumbnail,
  noBrandText,
  logoSelected,
  metaBoxContainer,
  metaBoxDeleteBtn
} from "./Sidebar.css";

/**
 * @typedef {object} Props
 * @prop {string} className
 * @prop {BrandRequest=} brandRequest
 * @prop {(files: {}[]) => void} onLogoSelected - handler get called when user wants to upload new logo version
 * @prop {() => void} onConfirmClick
 * @prop {() => void} onIgnoreClick
 * @prop {number} selectedBrandIndex
 * @prop {(newProps: object) => void} onBrandRequestChange
 * @prop {() => void} onBlur
 * @prop {string} industryId
 * @prop {string} geoCountryId
 * @prop {string} note
 * @prop {boolean} isConfirmDisabled
 * @prop {(metaKey: string, metaValue: string) => boolean} onAddNewMeta
 * @prop {(metaKey: string) => void} onDeleteMeta
 *
 *
 * @extends {Component<Props>}
 */
export default class Sidebar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      industries: [],
      countries: [],
      selectedThumbIndex: 0,
      metaKey: "",
      metaValue: ""
    };

    this.initialBrandState = {};
  }

  async componentWillMount() {
    const industries = await DataService.getIndustries();
    const countries = await DataService.getCountries();

    this.setState({
      industries,
      countries
    });
  }

  componentWillReceiveProps(nextProps) {
    const { brandRequest } = this.props;

    if (brandRequest !== nextProps.brandRequest) {
      this.setState({
        selectedThumbIndex: 0
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { selectedBrandIndex, industryId, geoCountryId } = this.props;

    if (selectedBrandIndex !== prevProps.selectedBrandIndex && this.isExistingBrand) {
      this.initialBrandState = {
        brandUrl: this.libraryContentWebsite,
        industryId,
        geoCountryId,
      }
    }
  }

  onAddNewLogoClick(event) {
    event.preventDefault();
    // Show file select dialog
    this.fileInput.click();
  }

  async onChangeFile(event) {
    const { onLogoSelected } = this.props;

    event.stopPropagation();
    event.preventDefault();

    // Copy them somewhere, because clearing the value also clears the fileList
    const files = Array.from(event.target.files);

    // Clear value so selecting the same file will trigger onChange
    this.fileInput.value = "";

    onLogoSelected(files);
  }

  onLogoVersionClick(index) {
    this.setState({
      selectedThumbIndex: index
    });
  }

  /**
   * @param {Partial<{ contentWebsite: string; industryId: string; countryId: string; }>} newProps
   */
  onBrandChange = (newProps) => {
    if (this.isExistingBrand) {
      try {
        if (newProps.contentWebsite !== undefined) {
          this.selectedBrand.logos[0].logoContent[0].website = newProps.contentWebsite;
        }
        if (newProps.industryId !== undefined) {
          this.selectedBrand.industry.id = newProps.industryId;
        }
        if (newProps.countryId !== undefined) {
          this.selectedBrand.logos[0].geoCountryId = newProps.countryId;
        }
      } catch (error) {
        console.error(error);
      }
    }
    this.props.onBrandRequestChange(this.isExistingBrand ? {} : newProps)
  }

  onBlur = () => {
    if (this.isExistingBrand) {
      try {
        const logoContent = this.selectedBrand.logos[0].logoContent[0];
        if (this.initialBrandState.brandUrl !== logoContent.website) {
          DataService.updateId(logoContent.id, { website: logoContent.website });
          this.initialBrandState.brandUrl = logoContent.website;
        }  
      } catch (error) { 
        console.error(error); 
        toast(
          "⚠️ Error updating Brand URL. Check Console for errors.",
          { type: toast.TYPE.ERROR, position: toast.POSITION.BOTTOM_RIGHT }
        );
      }
      
      try {  
        if (this.initialBrandState.industryId !== this.selectedBrand.industry.id) {
          DataService.updateId(this.selectedBrand.id, { industryId: id(this.selectedBrand.industry.id) });
          this.initialBrandState.industryId = this.selectedBrand.industry.id;
        }
      } catch (error) { 
        console.error(error); 
        toast(
          "⚠️ Error updating Industry. Check Console for errors.",
          { type: toast.TYPE.ERROR, position: toast.POSITION.BOTTOM_RIGHT }
        );
      }

      try {
        const logo = this.selectedBrand.logos[0];
        if (this.initialBrandState.geoCountryId !== logo.geoCountryId) {
          DataService.updateId(logo.id, { geoCountryId: id(this.selectedBrand.logos[0].geoCountryId) });
          this.initialBrandState.geoCountryId = logo.geoCountryId;
        }  
      } catch (error) { 
        console.error(error); 
        toast(
          "⚠️ Error updating Country. Check Console for errors.",
          { type: toast.TYPE.ERROR, position: toast.POSITION.BOTTOM_RIGHT }
        );
      }
    } else {
      this.props.onBlur();
    }
  }

  get isExistingBrand() {
    const { selectedBrandIndex } = this.props;
    const { selectedBrand } = this;

    return selectedBrandIndex === undefined || (selectedBrand && selectedBrand.id);
  }

  get selectedBrand() {
    const { selectedBrandIndex, brandRequest } = this.props;

    return brandRequest.brands[selectedBrandIndex];
  }

  get libraryContentWebsite() {
    const { selectedBrand } = this;

    return (selectedBrand &&
      selectedBrand.logos &&
      selectedBrand.logos.length &&
      selectedBrand.logos[0].logoContent &&
      selectedBrand.logos[0].logoContent.length &&
      selectedBrand.logos[0].logoContent[0].website) || "";
  }

  render() {
    const {
      industries,
      countries,
      selectedThumbIndex,
      metaKey,
      metaValue
    } = this.state;
    const {
      className,
      brandRequest = new BrandRequest(),
      onConfirmClick,
      onIgnoreClick,
      selectedBrandIndex,
      onBrandRequestChange,
      onBlur,
      industryId,
      geoCountryId,
      note,
      isConfirmDisabled,
      onAddNewMeta,
      onDeleteMeta
    } = this.props;
    const { selectedBrand, isExistingBrand } = this;

    const logoVersionRequests = brandRequest.logoVersionRequests.filter(
      lvr => selectedBrand !== undefined && lvr.brandId == selectedBrand.id // eslint-disable-line eqeqeq
    );
    const logoVersions = (selectedBrand && selectedBrand.logoVersions) || [];
    const displayedLogoVersions = logoVersions.concat(
      brandRequest.logoVersionRequests.filter(
        lvr =>
          !lvr.logoVersionId &&
          (!selectedBrand || (selectedBrand && lvr.brandId == selectedBrand.id)) // eslint-disable-line eqeqeq
      )
    );
    const previewUrl =
      displayedLogoVersions.length &&
      displayedLogoVersions[selectedThumbIndex].thumbUrl;

    const libraryContentWebsite =
      (selectedBrand &&
      selectedBrand.logos &&
      selectedBrand.logos.length &&
      selectedBrand.logos[0].logoContent &&
      selectedBrand.logos[0].logoContent.length &&
      selectedBrand.logos[0].logoContent[0].website) || "";

    return (
      <div className={className}>
        {/* Show Examples Button, Add New Logo, Ignore/Confirm buttons... */}
        <div className={`${row} ${mb2} ${alignItemsCenter}`}>
          <div className={`${colAuto}`}>
            {/* Show Examples Button */}
            <button
              type="submit"
              data-toggle="modal"
              data-target="#referenceImagesModal"
              className={`${btn} ${btnOutlineDark}`}
              disabled={!brandRequest.exampleUrls || brandRequest.exampleUrls.length === 0}
            >
              <i className="fa fa-image" aria-hidden="true" />
            </button>
          </div>
          <div className={`${colAuto} ${mlAuto}`}>
            {/* Add Logo Button */}
            <button
              type="submit"
              className={`${btn} ${btnSuccess}`}
              onClick={e => this.onAddNewLogoClick(e)}
              disabled={selectedBrandIndex === undefined}
            >
              <i className="fa fa-plus" aria-hidden="true" />
            </button>
            <input
              type="file"
              ref={input => {
                this.fileInput = input;
              }}
              style={{ display: "none" }}
              onChange={this.onChangeFile.bind(this)}
              multiple
            />
            {/* Ignore Button */}
            <button
              type="submit"
              className={`${btn} ${ml2} ${btnOutlineDark}`}
              onClick={onIgnoreClick}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
            {/* Confirm Button */}
            <button
              type="submit"
              className={`${btn} ${btnPrimary} ${ml2}`}
              onClick={onConfirmClick}
              disabled={isConfirmDisabled}
            >
              <i className="fa fa-check" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className={`${row}`}>
          <div className={`${col}`}>
            {/* Transparency grid */}
            <div
              style={{ backgroundImage: `url('${transparentGrid}')` }}
              className={`${transparencyGrid} ${dFlex}`}
            >
              {selectedBrandIndex === undefined && (
                <div className={`${noBrandText} ${px5} ${alignSelfCenter}`}>
                  NO BRAND CREATE NEW
                </div>
              )}
              {selectedBrandIndex !== undefined &&
                logoVersions.length === 0 &&
                logoVersionRequests.length === 0 && (
                  <div className={`${noBrandText} ${px5} ${alignSelfCenter}`}>
                    UPLOAD YOUR FIRST LOGO
                  </div>
                )}
              {/* Large Logo Preview */}
              {previewUrl !== 0 && (
                <div
                  style={{ backgroundImage: `url('${previewUrl}')` }}
                  className={`${logoPreview}`}
                />
              )}
            </div>
          </div>
        </div>
        {/* Logo Verion boxes */}
        <div className={`${row}`}>
          <div className={`${col}`}>
            {displayedLogoVersions.map((logoVersion, index) => (
              <div
                key={logoVersion.id}
                className={`${logoThumbnail} ${
                  selectedThumbIndex === index ? logoSelected : ""
                }`}
                style={{ backgroundImage: `url('${logoVersion.thumbUrl}')` }}
                onClick={() => this.onLogoVersionClick(index)}
              />
            ))}
          </div>
        </div>
        {/* Brand Name Input, Brand URL Input, Dropdowns, etc. */}
        <div className={`${row}`}>
          <div className={`${col}`}>
            <form>
              {/* Brand Name Input */}
              <div className={`${formGroup}`}>
                <label htmlFor="brandNameInput">Brand Name</label>
                <input
                  type="text"
                  className={`${formControl}`}
                  id="brandNameInput"
                  value={brandRequest.name}
                  disabled={isExistingBrand}
                  onChange={event =>
                    onBrandRequestChange({ name: event.target.value })
                  }
                  onBlur={onBlur}
                  placeholder="Apple"
                />
              </div>
              {/* Brand URL Input */}
              <div className={`${formGroup}`}>
                <label htmlFor="brandUrlInput">Brand URL</label>
                <input
                  type="text"
                  className={`${formControl}`}
                  id="brandUrlInput"
                  value={isExistingBrand ? libraryContentWebsite : brandRequest.contentWebsite}
                  onChange={event =>
                    this.onBrandChange({ contentWebsite: event.target.value })
                  }
                  onBlur={this.onBlur}
                  placeholder={isExistingBrand ? "" : "www.apple.com"}
                />
              </div>
              {/* Industry Dropdown */}
              <div className={`${formGroup}`}>
                <label htmlFor="industrySelect">Industry</label>
                <select
                  className={`${formControl}`}
                  id="industrySelect"
                  value={industryId}
                  onChange={event =>
                    this.onBrandChange({
                      industryId: event.target.value
                    })
                  }
                  onBlur={this.onBlur}
                >
                  <option>Select industry...</option>
                  {industries.map(industry => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Country Dropdown */}
              <div className={`${formGroup}`}>
                <label htmlFor="countrySelect">Country</label>
                <select
                  className={`${formControl}`}
                  id="countrySelect"
                  value={geoCountryId}
                  onChange={event =>
                    this.onBrandChange({
                      countryId: event.target.value
                    })
                  }
                  onBlur={this.onBlur}
                >
                  <option>Select country...</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Note */}
              <div className={`${formGroup}`}>
                <label htmlFor="noteTextArea">Note</label>
                <textarea
                  className={`${formControl}`}
                  id="noteTextArea"
                  value={note || ""}
                  onChange={event =>
                    onBrandRequestChange({ note: event.target.value })
                  }
                  rows={3}
                  onBlur={onBlur}
                />
              </div>
              {/* Meta Key */}
              <div className={`${formGroup}`}>
                <label htmlFor="metaKeyInput">Meta Key</label>
                <input
                  type="text"
                  className={`${formControl}`}
                  id="metaKeyInput"
                  value={metaKey}
                  onChange={event =>
                    this.setState({ metaKey: event.target.value })
                  }
                  maxLength={50}
                />
              </div>
              {/* Meta Value */}
              <div className={`${formGroup}`}>
                <label htmlFor="metaValueInput">Meta Value</label>
                <input
                  type="text"
                  className={`${formControl}`}
                  id="metaValueInput"
                  value={metaValue}
                  onChange={event =>
                    this.setState({ metaValue: event.target.value })
                  }
                  maxLength={300}
                />
                {/* Add New Meta Button */}
                <button
                  type="button"
                  className="float-right btn btn-link"
                  onClick={() => {
                    if (onAddNewMeta(metaKey, metaValue)) {
                      // Reset values
                      this.setState({
                        metaKey: "",
                        metaValue: ""
                      });
                    }
                  }}
                >
                  + Add New Meta
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* Meta Boxes */}
        {brandRequest.meta &&
          Object.keys(brandRequest.meta).map(key => (
            <div className={`${metaBoxContainer}`} key={key}>
              <div>{key}</div>
              <div>{brandRequest.meta[key]}</div>
              <button
                className={`${btn} ${btnLink} ${metaBoxDeleteBtn}`}
                onClick={() => onDeleteMeta(key)}
              >
                x
              </button>
            </div>
          ))}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Sidebar.js